// Dynamic meta tags utility for SEO
import { Helmet } from 'react-helmet';
import React from 'react';

/**
 * Generate meta tags for a page
 * @param {Object} config - Page configuration
 */
export const generateMetaTags = (config) => {
  const {
    title,
    description,
    keywords = [],
    canonicalUrl,
    ogType = 'website',
    ogImage = 'https://draggintrailers.com/og-image.jpg',
    twitterCard = 'summary_large_image',
    structuredData = null,
    noindex = false
  } = config;

  const fullTitle = title ? `${title} | Draggin Trailers` : 'Draggin Trailers - Rent Trailers from Local Owners';
  const fullDescription = description || 'Find and rent trailers near you. Browse cargo, utility, enclosed, and specialty trailers from verified local owners.';
  const fullCanonicalUrl = canonicalUrl || 'https://draggintrailers.com';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      {keywords?.length > 0 && <meta name="keywords" content={keywords?.join(', ')} />}
      <link rel="canonical" href={fullCanonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content="Draggin Trailers" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Page-specific meta tag configurations
 */
export const pageMetaConfigs = {
  'home-page': {
    title: 'Home',
    description: 'Find and rent trailers near you. Browse cargo trailers, utility trailers, enclosed trailers, and specialty equipment from verified local owners. Affordable daily and weekly rates.',
    keywords: ['trailer rental', 'rent trailer', 'cargo trailer', 'utility trailer', 'enclosed trailer', 'equipment rental', 'peer-to-peer rental'],
    canonicalUrl: 'https://draggintrailers.com'
  },
  'search-results': {
    title: 'Search Trailers',
    description: 'Browse available trailers for rent. Filter by type, location, size, and price. Find the perfect trailer for your needs.',
    keywords: ['search trailers', 'find trailer rental', 'trailer availability', 'rent trailer near me'],
    canonicalUrl: 'https://draggintrailers.com/search-results'
  },
  'renter-dashboard': {
    title: 'Renter Dashboard',
    description: 'Manage your trailer rentals, view booking history, track active rentals, and communicate with owners.',
    canonicalUrl: 'https://draggintrailers.com/renter-dashboard',
    noindex: true
  },
  'owner-dashboard': {
    title: 'Owner Dashboard',
    description: 'Manage your trailer listings, view bookings, track earnings, and communicate with renters.',
    canonicalUrl: 'https://draggintrailers.com/owner-dashboard',
    noindex: true
  },
  'admin-dashboard': {
    title: 'Admin Dashboard',
    description: 'Platform administration dashboard for managing users, listings, disputes, and platform metrics.',
    canonicalUrl: 'https://draggintrailers.com/admin-dashboard',
    noindex: true
  },
  'admin-settings': {
    title: 'Admin Settings',
    description: 'Configure platform rules, fee structure, verification requirements, and policy management.',
    canonicalUrl: 'https://draggintrailers.com/admin-settings',
    noindex: true
  },
  'admin-platform-management': {
    title: 'Platform Management',
    description: 'Monitor platform activity, manage disputes, verify owners, and oversee marketplace operations.',
    canonicalUrl: 'https://draggintrailers.com/admin-platform-management',
    noindex: true
  },
  'messaging-center': {
    title: 'Messages',
    description: 'Communicate with trailer owners and renters. View conversations and send messages.',
    canonicalUrl: 'https://draggintrailers.com/messaging-center',
    noindex: true
  },
  'reviews-management': {
    title: 'Reviews Management',
    description: 'Submit and manage reviews for completed rentals. View ratings and feedback.',
    canonicalUrl: 'https://draggintrailers.com/reviews-management',
    noindex: true
  },
  'trailer-listing-creation': {
    title: 'List Your Trailer',
    description: 'List your trailer for rent. Add photos, specifications, pricing, and availability.',
    keywords: ['list trailer', 'rent out trailer', 'trailer owner', 'earn money trailer'],
    canonicalUrl: 'https://draggintrailers.com/trailer-listing-creation'
  },
  'payout-management': {
    title: 'Payout Management',
    description: 'Manage your earnings, view payout history, and configure direct deposit settings.',
    canonicalUrl: 'https://draggintrailers.com/payout-management',
    noindex: true
  },
  'dispute-resolution': {
    title: 'Dispute Resolution',
    description: 'File and manage rental disputes. Submit evidence and track resolution status.',
    canonicalUrl: 'https://draggintrailers.com/dispute-resolution',
    noindex: true
  },
  'charge-dispute': {
    title: 'Charge Dispute',
    description: 'Dispute rental charges and violation fees. Submit evidence and track dispute status.',
    canonicalUrl: 'https://draggintrailers.com/charge-dispute',
    noindex: true
  },
  'owner-dispute-review': {
    title: 'Owner Dispute Review',
    description: 'Review and respond to renter disputes. Submit counter-evidence and track resolution.',
    canonicalUrl: 'https://draggintrailers.com/owner-dispute-review',
    noindex: true
  },
  'terms-of-service': {
    title: 'Terms of Service',
    description: 'Read our terms of service and user agreement for the Draggin Trailers platform.',
    canonicalUrl: 'https://draggintrailers.com/terms-of-service'
  },
  'privacy-policy': {
    title: 'Privacy Policy',
    description: 'Learn how we collect, use, and protect your personal information.',
    canonicalUrl: 'https://draggintrailers.com/privacy-policy'
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    description: 'Information about how we use cookies and similar technologies.',
    canonicalUrl: 'https://draggintrailers.com/cookie-policy'
  },
  'state-towing-regulations': {
    title: 'State Towing Regulations',
    description: 'View towing regulations and requirements by state. Stay compliant with local laws.',
    keywords: ['towing regulations', 'state towing laws', 'trailer regulations', 'towing requirements'],
    canonicalUrl: 'https://draggintrailers.com/state-towing-regulations'
  }
};

/**
 * Get meta tags for a specific page
 * @param {string} pageName - Name of the page
 * @param {Object} customConfig - Custom configuration to override defaults
 */
export const getPageMetaTags = (pageName, customConfig = {}) => {
  const defaultConfig = pageMetaConfigs?.[pageName] || {};
  const config = { ...defaultConfig, ...customConfig };
  return generateMetaTags(config);
};

export default {
  generateMetaTags,
  getPageMetaTags,
  pageMetaConfigs
};